import React, { useRef, useEffect, useState } from "react";
import classNames from "classnames";
import styles from "./RichTextEditor.module.scss";
import Icon from "../images/Icon";

export interface RichTextEditorProps {
  value?: string;
  onChange: (value: string) => void;
  width?: string;
  height?: string;
  glowColor?: string;
  borderColor?: string;
  borderRadius?: string;
  fontSize?: string;
  toolbarBgColor?: string;
  iconColor?: string;
  iconHoverColor?: string;
  iconActiveColor?: string;
}

const RichTextEditor = ({
  value          = "",
  onChange,
  width          = "100%",
  height         = "fit-content",
  glowColor      = "var(--color-RichTextEditor-Focus-normal)",
  borderColor    = "var(--color-RichTextEditor-Border-normal)",
  borderRadius   = "5px",
  fontSize       = "16px",
  toolbarBgColor = "var(--color-RichTextEditor-ToolBar-BG-normal)",
  iconColor      = "var(--color-RichTextEditor-ToolBar-Icon-normal)",
  iconHoverColor = "var(--color-RichTextEditor-ToolBar-Icon-Hover-normal)",
  iconActiveColor = "var(--color-RichTextEditor-ToolBar-Icon-Selected-normal)",
}: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeCommands, setActiveCommands] = useState<Set<string>>(new Set());
  const [, forceUpdate] = useState({});

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const updateActiveCommands = () => {
    const commands = ["bold", "italic", "underline", "insertOrderedList", "insertUnorderedList"];
    const active = new Set<string>();
    commands.forEach((cmd) => {
      if (document.queryCommandState(cmd)) {
        active.add(cmd);
      }
    });
    setActiveCommands(active);
  };

  const executeCommand = (command: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    triggerChange();
    updateActiveCommands();
    forceUpdate({});
  };

  const triggerChange = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    triggerChange();
  };

  const handleKeyUp = () => {
    updateActiveCommands();
  };

  const handleMouseUp = () => {
    updateActiveCommands();
  };

  const handleToolbarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const getIconColor = (command: string) =>
    activeCommands.has(command) ? iconActiveColor : iconColor;

  const getIconHoverColor = (command: string) =>
    activeCommands.has(command) ? iconActiveColor : iconHoverColor;

  return (
    <div
      className={classNames(styles.editor)}
      style={{
        width,
        height,
        borderColor,
        borderRadius,
        "--glow-color": glowColor,
      } as React.CSSProperties}
    >
      <div
        className={classNames(styles.toolbar)}
        style={{ backgroundColor: toolbarBgColor }}
        onMouseDown={handleToolbarMouseDown}
      >
        {[
          { command: "bold",               icon: "bold"           },
          { command: "italic",             icon: "italic"         },
          { command: "underline",          icon: "underline"      },
          { command: "insertOrderedList",  icon: "orderedList"    },
          { command: "insertUnorderedList",icon: "unorderedList"  },
        ].map(({ command, icon }) => (
          <span
            key={command}
            onClick={() => executeCommand(command)}
            className={classNames(styles.toolbarBtn, {
              [styles.active]: activeCommands.has(command),
            })}
          >
            <Icon
              name={icon}
              color={getIconColor(command)}
              hoverColor={getIconHoverColor(command)}
            />
          </span>
        ))}

        <span
          className={classNames(styles.toolbarBtn)}
          onClick={() => {
            const url = prompt("Enter the URL:");
            if (url) executeCommand("createLink", url);
          }}
        >
          <Icon name="insertLink" color={iconColor} hoverColor={iconHoverColor} />
        </span>

        <span
          className={classNames(styles.toolbarBtn)}
          onClick={() => {
            const url = prompt("Enter image URL:");
            if (url) executeCommand("insertImage", url);
          }}
        >
          <Icon name="insertImage" color={iconColor} hoverColor={iconHoverColor} />
        </span>
      </div>

      <div
        ref={editorRef}
        contentEditable
        className={classNames(styles.content)}
        style={{ fontSize }}
        onInput={handleInput}
        onKeyUp={handleKeyUp}
        onMouseUp={handleMouseUp}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;